import produce from 'immer';
import {
  types,
  CartState,
  CartActionTypes,
  ReserveTicketsAsyncSuccess,
  ReserveTicketsAsyncPending,
  ReserveTicketsAsyncError,
  ReserveMembershipAsyncRequest,
  ReserveMembershipAsyncSuccess,
  ReserveMembershipAsyncPending,
  ReserveMembershipAsyncError,
  ReleaseCartAsyncPending,
  PurchaseCartAsyncPending,
  PurchaseCartAsyncSuccess,
  PurchaseCartAsyncError,
  ApplyPromoCodeAsyncRequest,
  ApplyPromoCodeAsyncError,
  ApplyPromoCodeAsyncPending,
  ApplyPromoCodeAsyncSuccess,
  ApplyGiftVoucherAsyncRequest,
  ApplyGiftVoucherAsyncError,
  ApplyGiftVoucherAsyncPending,
  ApplyGiftVoucherAsyncSuccess,
  RemoveTicketsAsyncRequest,
  RemoveTicketsAsyncError,
  RemoveTicketsAsyncPending,
  RemoveTicketsAsyncSuccess,
  ApplyDonationAsyncRequest,
  ApplyDonationAsyncError,
  ApplyDonationAsyncPending,
  ApplyDonationAsyncSuccess,
  RemoveDonationAsyncRequest,
  RemoveDonationAsyncError,
  RemoveDonationAsyncPending,
  RemoveDonationAsyncSuccess,
  SetDonationSource
} from './types';

// Actions
export const actions = {
  reserveTickets: (payload: any[]): CartActionTypes => ({
    type: types.RESERVE_TICKETS_ASYNC.REQUEST,
    payload
  }),
  reserveMembership: (payload: ReserveMembershipAsyncRequest['payload']): CartActionTypes => ({
    type: types.RESERVE_MEMBERSHIP_ASYNC.REQUEST,
    payload
  }),
  releaseCart: (payload: any): CartActionTypes => ({
    type: types.RELEASE_CART_ASYNC.REQUEST,
    payload
  }),
  resetCart: (): CartActionTypes => ({ type: types.RESET_CART }),
  purchaseCart: () => ({ type: types.PURCHASE_CART_ASYNC.REQUEST }),
  resetPurchaseError: () => ({ type: types.RESET_PURCHASE_MESSAGE }),
  applyPromoCode: (payload: ApplyPromoCodeAsyncRequest['payload']): CartActionTypes => ({
    type: types.APPLY_PROMO_CODE_ASYNC.REQUEST,
    payload
  }),
  applyGiftVoucher: (payload: ApplyGiftVoucherAsyncRequest['payload']): CartActionTypes => ({
    type: types.APPLY_GIFT_VOUCHER_ASYNC.REQUEST,
    payload
  }),
  removeTicketsFromCart: (payload: RemoveTicketsAsyncRequest['payload']): CartActionTypes => ({
    type: types.REMOVE_TICKETS_FROM_CART.REQUEST,
    payload
  }),
  applyDonation: (payload: ApplyDonationAsyncRequest['payload']): CartActionTypes => ({
    type: types.APPLY_DONATION_ASYNC.REQUEST,
    payload
  }),
  removeDonation: (payload: RemoveDonationAsyncRequest['payload']): CartActionTypes => ({
    type: types.REMOVE_DONATION_ASYNC.REQUEST,
    payload
  }),
  setDonationSource: (payload: SetDonationSource['payload']): CartActionTypes => ({
    type: types.SET_DONATION_SOURCE,
    payload
  }),
  resetPromoCode: (): CartActionTypes => ({ type: types.RESET_PROMO_CODE }),
  resetGiftVoucher: (): CartActionTypes => ({ type: types.RESET_GIFT_VOUCHER }),
  resetCartError: (): CartActionTypes => ({ type: types.RESET_CART_ERROR }),
  resetDonationError: (): CartActionTypes => ({ type: types.RESET_DONATION_ERROR })
};

// Initial State
export const initialState: CartState = {
  cart: null,
  purchased: false,
  reserving: false,
  loading: false,
  error: undefined,
  checkoutCart: null,
  purchaseError: undefined,
  promoCode: {
    loading: false,
    error: undefined,
    processed: false
  },
  giftVoucher: {
    loading: false,
    error: undefined,
    processed: false
  },
  donation: {
    source: null,
    error: undefined
  }
};

// Reducer
export const reducer = (state: CartState = initialState, action: CartActionTypes): CartState => {
  // eslint-disable-next-line consistent-return
  return produce(state, (draft) => {
    switch (action.type) {
      case types.RESERVE_TICKETS_ASYNC.PENDING:
      case types.RESERVE_MEMBERSHIP_ASYNC.PENDING:
      case types.APPLY_DONATION_ASYNC.PENDING:
        draft.reserving = (
          action as ReserveTicketsAsyncPending | ReserveMembershipAsyncPending | ApplyDonationAsyncPending
        ).payload;
        break;
      case types.RELEASE_CART_ASYNC.PENDING:
      case types.PURCHASE_CART_ASYNC.PENDING:
      case types.REMOVE_TICKETS_FROM_CART.PENDING:
      case types.REMOVE_DONATION_ASYNC.PENDING:
        draft.loading = (
          action as
            | ReleaseCartAsyncPending
            | PurchaseCartAsyncPending
            | RemoveTicketsAsyncPending
            | RemoveDonationAsyncPending
        ).payload;
        break;
      case types.APPLY_PROMO_CODE_ASYNC.PENDING:
        draft.promoCode.loading = (action as ApplyPromoCodeAsyncPending).payload;
        break;
      case types.APPLY_GIFT_VOUCHER_ASYNC.PENDING:
        draft.giftVoucher.loading = (action as ApplyGiftVoucherAsyncPending).payload;
        break;
      case types.PURCHASE_CART_ASYNC.SUCCESS:
        draft.purchased = true;
        draft.checkoutCart = (action as PurchaseCartAsyncSuccess).payload;
        draft.error = undefined;
        break;
      case types.RESERVE_TICKETS_ASYNC.SUCCESS:
      case types.RESERVE_MEMBERSHIP_ASYNC.SUCCESS:
      case types.APPLY_DONATION_ASYNC.SUCCESS:
      case types.REMOVE_TICKETS_FROM_CART.SUCCESS:
        draft.cart = (
          action as
            | ReserveTicketsAsyncSuccess
            | ReserveMembershipAsyncSuccess
            | ApplyDonationAsyncSuccess
            | RemoveTicketsAsyncSuccess
        ).payload;
        draft.error = undefined;
        draft.donation.error = undefined;
        break;
      case types.REMOVE_DONATION_ASYNC.SUCCESS:
        draft.cart = (action as RemoveDonationAsyncSuccess).payload;
        draft.donation.source = null;
        break;
      case types.APPLY_PROMO_CODE_ASYNC.SUCCESS:
        const { processed, updatedCart } = (action as ApplyPromoCodeAsyncSuccess).payload;
        draft.promoCode.processed = processed;
        draft.cart = updatedCart;
        break;
      case types.APPLY_GIFT_VOUCHER_ASYNC.SUCCESS:
        draft.giftVoucher = (action as ApplyGiftVoucherAsyncSuccess).payload;
        break;
      case types.RESERVE_TICKETS_ASYNC.ERROR:
      case types.RESERVE_MEMBERSHIP_ASYNC.ERROR:
      case types.REMOVE_TICKETS_FROM_CART.ERROR:
        draft.error = (
          action as ReserveTicketsAsyncError | ReserveMembershipAsyncError | RemoveTicketsAsyncError
        ).payload;
        break;
      case types.APPLY_DONATION_ASYNC.ERROR:
      case types.REMOVE_DONATION_ASYNC.ERROR:
        draft.donation.error = (action as ApplyDonationAsyncError | RemoveDonationAsyncError).payload;
        break;
      case types.PURCHASE_CART_ASYNC.ERROR:
        draft.purchaseError = (action as PurchaseCartAsyncError).payload;
        break;
      case types.APPLY_PROMO_CODE_ASYNC.ERROR:
        draft.promoCode.error = (action as ApplyPromoCodeAsyncError).payload;
        break;
      case types.APPLY_GIFT_VOUCHER_ASYNC.ERROR:
        draft.giftVoucher = (action as ApplyGiftVoucherAsyncError).payload;
        break;
      case types.SET_DONATION_SOURCE:
        draft.donation.source = (action as SetDonationSource).payload;
        break;
      case types.RELEASE_CART_ASYNC.SUCCESS:
      case types.RELEASE_CART_ASYNC.ERROR: // TODO: handle release cart error more robustly
      case types.RESET_CART:
        return initialState;
      case types.RESET_PROMO_CODE:
        draft.promoCode = initialState.promoCode;
        break;
      case types.RESET_GIFT_VOUCHER:
        draft.giftVoucher = initialState.giftVoucher;
        break;
      case types.RESET_CART_ERROR:
        draft.error = undefined;
        break;
      case types.RESET_DONATION_ERROR:
        draft.donation.error = undefined;
        break;
      case types.RESET_PURCHASE_MESSAGE:
        draft.purchaseError = undefined;
        break;
      default:
    }
  });
};
